import * as React from 'react';
import { Input, Toggle, Tooltip } from 'vet-bones/design-system/components';

import { TEST_IDS } from 'src/constants/testIds';
import {
  SqlQuotingConvention,
  VirtualGraphOption,
} from 'src/store/state/dataSources';
import { ProjectVirtualGraphOptions } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useFlushedDebouncedCallback } from 'src/utils/hooks/useFlushedDebouncedCallback';

import styles from 'src/components/resourceDialog/styles/projectResourceOptionsForm.module.scss';

type VirtualGraphOptionsFormProps = {
  virtualGraphOptions: ProjectVirtualGraphOptions;
  onSetVirtualGraphOptions: (options: ProjectVirtualGraphOptions) => void;
};

export const VirtualGraphOptionsForm: React.VFC<
  VirtualGraphOptionsFormProps
> = ({ virtualGraphOptions, onSetVirtualGraphOptions }) => {
  const [localSqlQuoting, setLocalSqlQuoting] = React.useState<string>(
    virtualGraphOptions[VirtualGraphOption.SQL_QUOTING_CONVENTION] || ''
  );
  const [localSqlFunctions, setLocalSqlFunctions] = React.useState(
    virtualGraphOptions[VirtualGraphOption.SQL_FUNCTIONS] || ''
  );

  const debouncedSetSqlQuoting = useFlushedDebouncedCallback(
    (value: string) => {
      onSetVirtualGraphOptions({
        ...virtualGraphOptions,
        [VirtualGraphOption.SQL_QUOTING_CONVENTION]:
          value as SqlQuotingConvention,
      });
    },
    500
  );

  const debouncedSetSqlFunctions = useFlushedDebouncedCallback(
    (value: string) => {
      onSetVirtualGraphOptions({
        ...virtualGraphOptions,
        [VirtualGraphOption.SQL_FUNCTIONS]: value,
      });
    },
    500
  );

  const handleQuotingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalSqlQuoting(value);
    debouncedSetSqlQuoting(value);
  };

  const handleSqlFunctionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalSqlFunctions(value);
    debouncedSetSqlFunctions(value);
  };

  const handlePercentEncodeChange = () => {
    onSetVirtualGraphOptions({
      ...virtualGraphOptions,
      [VirtualGraphOption.PERCENT_ENCODE]:
        !virtualGraphOptions[VirtualGraphOption.PERCENT_ENCODE],
    });
  };

  return (
    <div className={styles.projectResourceOptionsForm}>
      <Input
        clearable={false}
        id="sql-quoting-convention"
        label={
          copy.components.projectResourceDialog.projectVirtualGraphPanel
            .resourceProperties[VirtualGraphOption.SQL_QUOTING_CONVENTION].label
        }
        onChange={handleQuotingChange}
        placeholder={SqlQuotingConvention.NATIVE}
        size="md"
        testId={
          TEST_IDS.projectVirtualGraphPanel.resourceProperties
            .sqlQuotingConvention
        }
        value={localSqlQuoting}
      />
      <Input
        clearable={false}
        id="sql-functions"
        label={
          copy.components.projectResourceDialog.projectVirtualGraphPanel
            .resourceProperties[VirtualGraphOption.SQL_FUNCTIONS].label
        }
        onChange={handleSqlFunctionsChange}
        placeholder=""
        size="md"
        testId={
          TEST_IDS.projectVirtualGraphPanel.resourceProperties.sqlFunctions
        }
        value={localSqlFunctions}
      />
      <div className={styles.toggleField}>
        <Tooltip
          description={
            copy.components.projectResourceDialog.projectVirtualGraphPanel
              .resourceProperties[VirtualGraphOption.PERCENT_ENCODE].description
          }
        >
          <Toggle
            checked={Boolean(
              virtualGraphOptions[VirtualGraphOption.PERCENT_ENCODE]
            )}
            id="percent-encode"
            onChange={handlePercentEncodeChange}
            size="md"
            testId={
              TEST_IDS.projectVirtualGraphPanel.resourceProperties.percentEncode
            }
          />
        </Tooltip>
        <label className={styles.toggleLabel} htmlFor="percent-encode">
          {
            copy.components.projectResourceDialog.projectVirtualGraphPanel
              .resourceProperties[VirtualGraphOption.PERCENT_ENCODE].label
          }
        </label>
      </div>
    </div>
  );
};
